import { useEffect, useState } from 'react'
import { Bot } from 'lucide-react'

const SPINNER_VERBS = [
  'Accomplishing', 'Actioning', 'Actualizing', 'Architecting', 'Baking', 'Beaming', "Beboppin'",
  'Befuddling', 'Billowing', 'Blanching', 'Bloviating', 'Boogieing', 'Boondoggling', 'Booping',
  'Bootstrapping', 'Brewing', 'Bunning', 'Burrowing', 'Calculating', 'Canoodling', 'Caramelizing',
  'Cascading', 'Catapulting', 'Cerebrating', 'Channeling', 'Choreographing', 'Churning', 'Clauding',
  'Coalescing', 'Cogitating', 'Combobulating', 'Composing', 'Computing', 'Concocting', 'Considering',
  'Contemplating', 'Cooking', 'Crafting', 'Creating', 'Crunching', 'Crystallizing', 'Cultivating',
  'Deciphering', 'Deliberating', 'Determining', 'Dilly-dallying', 'Discombobulating', 'Doing',
  'Doodling', 'Drizzling', 'Ebbing', 'Effecting', 'Elucidating', 'Embellishing', 'Enchanting',
  'Envisioning', 'Evaporating', 'Fermenting', 'Fiddle-faddling', 'Finagling', 'Flambéing',
  'Flibbertigibbeting', 'Flowing', 'Flummoxing', 'Fluttering', 'Forging', 'Forming', 'Frolicking',
  'Frosting', 'Gallivanting', 'Galloping', 'Garnishing', 'Generating', 'Gesticulating', 'Germinating',
  'Gitifying', 'Grooving', 'Gusting', 'Harmonizing', 'Hashing', 'Hatching', 'Herding', 'Honking',
  'Hullaballooing', 'Hyperspacing', 'Ideating', 'Imagining', 'Improvising', 'Incubating', 'Inferring',
  'Infusing', 'Ionizing', 'Jitterbugging', 'Julienning', 'Kneading', 'Leavening', 'Levitating',
  'Lollygagging', 'Manifesting', 'Marinating', 'Meandering', 'Metamorphosing', 'Misting',
  'Moonwalking', 'Moseying', 'Mulling', 'Mustering', 'Musing', 'Nebulizing', 'Nesting', 'Newspapering',
  'Noodling', 'Nucleating', 'Orbiting', 'Orchestrating', 'Osmosing', 'Perambulating', 'Percolating',
  'Perusing', 'Philosophising', 'Photosynthesizing', 'Pollinating', 'Pondering', 'Pontificating',
  'Pouncing', 'Precipitating', 'Prestidigitating', 'Processing', 'Proofing', 'Propagating',
  'Puttering', 'Puzzling', 'Quantumizing', 'Razzle-dazzling', 'Razzmatazzing', 'Recombobulating',
  'Reticulating', 'Roosting', 'Ruminating', 'Sautéing', 'Scampering', 'Schlepping', 'Scurrying',
  'Seasoning', 'Shenaniganing', 'Shimmying', 'Simmering', 'Skedaddling', 'Sketching', 'Slithering',
  'Smooshing', 'Sock-hopping', 'Spelunking', 'Spinning', 'Sprouting', 'Stewing', 'Sublimating',
  'Swirling', 'Swooping', 'Symbioting', 'Synthesizing', 'Tempering', 'Thinking', 'Thundering',
  'Tinkering', 'Tomfoolering', 'Topsy-turvying', 'Transfiguring', 'Transmuting', 'Twisting',
  'Undulating', 'Unfurling', 'Unravelling', 'Vibing', 'Waddling', 'Wandering', 'Warping',
  'Whatchamacalliting', 'Whirlpooling', 'Whirring', 'Whisking', 'Wibbling', 'Working', 'Wrangling',
  'Zesting', 'Zigzagging',
]

function pickVerb(): string {
  return SPINNER_VERBS[crypto.getRandomValues(new Uint32Array(1))[0] % SPINNER_VERBS.length]
}

export function NodeThinkingSpinner({ nodeName }: { nodeName?: string | null }) {
  const [verb, setVerb] = useState<string>(() => pickVerb())

  useEffect(() => {
    const id = setInterval(() => setVerb(pickVerb()), 2500)
    return () => clearInterval(id)
  }, [])

  return (
    <div className="py-5" role="status" aria-live="polite" data-testid="node-thinking-spinner">
      <style>{BOUNCE_KEYFRAMES}</style>
      <div className="flex gap-3 items-start">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-button text-button-foreground mt-0.5">
          <Bot className="h-4 w-4" />
        </div>
        <div className="flex items-baseline gap-1 text-sm text-muted-foreground pt-1">
          <span className="italic">{verb}</span>
          <span className="inline-flex items-end gap-[3px] pb-[2px] ml-1" aria-hidden="true">
            <span style={dotStyle(0)} className="block h-1 w-1 rounded-full bg-current" />
            <span style={dotStyle(1)} className="block h-1 w-1 rounded-full bg-current" />
            <span style={dotStyle(2)} className="block h-1 w-1 rounded-full bg-current" />
          </span>
          {nodeName ? <span className="ml-2 text-xs opacity-60">· {nodeName}</span> : null}
        </div>
      </div>
    </div>
  )
}

const BOUNCE_KEYFRAMES = `
@keyframes idsp-dot-bounce {
  0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
  40% { transform: translateY(-3px); opacity: 1; }
}
`

function dotStyle(index: number): React.CSSProperties {
  return {
    animation: 'idsp-dot-bounce 1.2s infinite ease-in-out',
    animationDelay: `${index * 0.15}s`,
  }
}
